import { Component, HostListener, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { AccountService } from '../../../../../services/account/account.service';
import { environment } from '../../../../../../environments/environment';
import {
  OrganizationConfig
} from '../../../../../models/organization-config';
import { OrganizationConfigService } from '../../../../../services/organization-config/organization-config.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { CallbackResult, CallbackResultStatus, scrollToElement } from '../../../../../utils';
import { SetMailchimpAudienceModalComponent } from './modals/set-mailchimp-audience-modal/set-mailchimp-audience-modal.component';
import { ApiService } from '../../../../../services/api/api.service';
import { PreviewEmailOptInModalComponent } from './modals/preview-email-opt-in-modal/preview-email-opt-in-modal.component';
import { SetSquareLocationMapModalComponent } from './modals/set-square-location-map-modal/set-square-location-map-modal.component';
import { SetSquareCustomerGroupsManagedByCapitanModalComponent } from './modals/set-square-customer-groups-managed-by-capitan-modal/set-square-customer-groups-managed-by-capitan-modal.component';
import { SetLightspeedCustomerGroupsManagedByCapitanModalComponent } from './modals/set-lightspeed-customer-groups-managed-by-capitan-modal/set-lightspeed-customer-groups-managed-by-capitan-modal.component';
import {
  SetLightspeedLocationMapModalComponent
} from './modals/set-lightspeed-location-map-modal/set-lightspeed-location-map-modal.component';
import {
  BarcodeFormat,
  MailchimpSyncWindow,
  OrgPOSSoftware, PaymentProcessorType,
  ReportingFirstDayOfWeek
} from '../../../../../models/organization-config-enums';
import { ComponentHasUnsavedChanges } from '../../../../../unsaved-changes-guard.service';
import {
  PreviewInviteEmailModalComponent
} from './modals/preview-invite-email-modal/preview-invite-email-modal.component';
import {
  SetCapitanLightspeedUserModalComponent
} from './modals/set-capitan-lightspeed-user-modal/set-capitan-lightspeed-user-modal.component';

@Component({
  selector: 'app-organization-settings',
  templateUrl: './organization-settings.component.html',
  styleUrls: ['./organization-settings.component.css']
})
export class OrganizationSettingsComponent implements OnInit, ComponentHasUnsavedChanges {

  public orgConfig: OrganizationConfig;

  /**
   * The websites allowed to embed the Climber App (PRO-1422), as one address
   * per line. A textarea rather than a repeating row of inputs because that
   * is what this setting is in practice: a short list someone pastes in once.
   * Matches the freeze/cancellation reason editors.
   */
  public embedDomainsInput = '';

  public floatingSuccessMessage = '';
  public floatingErrorMessage = '';

  public orgConfigErrors: any = {};
  public disconnectStripeAccountErrorMessage = '';
  public disconnectLightspeedAccountErrorMessage = '';
  public disconnectSquareAccountErrorMessage = '';
  public mailchimpErrorMessage = '';
  public mailchimpSuccessMessage = '';

  public loadingOrgConfig = true;
  public loadingClearStripe = false;
  public loadingClearLightspeed = false;
  public loadingClearSquare = false;
  public loadingClearMailchimp = false;
  public loadingResyncMailchimpGroups = false;
  public loadingSubmit = false;
  public savingMailchimpConfig = false;

  public serverError: number = null;
  public unsavedChanges = false;

  public currentSectionId = 'staff-site-settings';
  public readonly scrollToElement = scrollToElement;

  public modalResult: CallbackResult = null;

  public OrgPOSSoftware = OrgPOSSoftware;
  public CallbackResultStatus = CallbackResultStatus;
  public ReportingFirstDayOfWeek = ReportingFirstDayOfWeek;
  public MailchimpSyncWindow = MailchimpSyncWindow
  public BarcodeFormat = BarcodeFormat;

  constructor(
    public apiService: ApiService,
    private accountService: AccountService,
    private orgConfigService: OrganizationConfigService,
    private modalService: NgbModal,
    private route: ActivatedRoute
  ) {}

  ngOnInit() {
    this.loadOrganizationConfig();
  }

  hasUnsavedChanges() {
    return this.unsavedChanges;
  }

  public onSectionChange(sectionId: string) {
    this.currentSectionId = sectionId;
  }

  @HostListener('window:beforeunload')
  allowUnload() {
    return !this.hasUnsavedChanges();
  }

  /**
   * Load the organization config from the API.
   *
   * We do not use the local-stored copy, in order to avoid pop-in of changed
   * values.
   */
  public loadOrganizationConfig() {
    this.loadingOrgConfig = true;

    this.orgConfigService
      .loadOrganizationConfig()
      .then(() => {
        this.orgConfig = this.orgConfigService.getOrganizationConfig();
        const orgConfigServerError = this.orgConfigService.getOrganizationConfigServerError();
        if (orgConfigServerError) {
          this.serverError = orgConfigServerError;
        }
        // loadOrganizationConfig() swallows a failed request and RESOLVES, so
        // the config can be null here; an unguarded dereference would skip
        // the loadingOrgConfig reset and leave the page on a permanent spinner.
        if (this.orgConfig) {
          this.syncEmbedDomainsInput();
        }
        this.loadingOrgConfig = false;
        this.scrollToRouteFragment();
      });
  }

  /**
   * Scroll to the section named by the route's URL fragment, e.g. a link from
   * another page with `fragment="climber-app-settings"`. The router leaves
   * anchors unhandled (see ScrollPositionService), and the target only exists
   * once the loading guard around the form clears - hence the setTimeout.
   */
  private scrollToRouteFragment() {
    const fragment = this.route.snapshot.fragment;
    if (!fragment) { return; }

    setTimeout(() => {
      if (document.getElementById(fragment)) {
        this.scrollToElement(fragment);
      }
    });
  }

  /**
   * Fill the embed-domain textarea from the loaded config.
   */
  private syncEmbedDomainsInput() {
    this.embedDomainsInput =
      (this.orgConfig.climber_app_embed_domains || []).join('\n');
  }

  /**
   * Read the embed-domain textarea back into the config.
   *
   * Blank lines and surrounding whitespace are dropped here so that the
   * ordinary shapes a textarea produces don't come back as a validation
   * error. Everything beyond that - lowercasing, de-duplicating, rejecting
   * anything that isn't a bare hostname - is the server's job, since the API
   * is what every other caller meets.
   */
  private readEmbedDomainsInput(): string[] {
    return this.embedDomainsInput
      .split(/[\r\n]+/)
      .map(domain => domain.trim())
      .filter(domain => domain.length > 0);
  }

  public getLightspeedOAuthInitURL() {
    return environment.api_url + 'vend/oauth-init/?token=' + this.accountService.getToken();
  }

  public getSquareOAuthInitURL() {
    return environment.api_url + 'square/oauth-init/?token=' + this.accountService.getToken();
  }

  public getMailchimpOAuthInitURL() {
    return environment.api_url + 'mailchimp/oauth-init/?token=' + this.accountService.getToken();
  }

  public onGenerateBarcodesAutomaticallyChange() {
    this.unsavedChanges = true;
    // Clear the dependent age-limit options when auto-generation is turned off.
    if (!this.orgConfig.generate_barcodes_automatically) {
      this.orgConfig.limit_generated_barcodes_by_age = false;
      this.orgConfig.minimum_barcode_age = null;
    }
  }

  public onLimitBarcodesByAgeChange() {
    this.unsavedChanges = true;
    // Clear the age when the age limit is turned off.
    if (!this.orgConfig.limit_generated_barcodes_by_age) {
      this.orgConfig.minimum_barcode_age = null;
    }
  }

  public saveOrganizationSettings() {
    // Not loadingOrgConfig: that flag gates the *ngIf around the whole form,
    // and tearing the form out resets the scroll position, so a validation
    // error would land with the page scrolled to the top.
    this.loadingSubmit = true;

    this.floatingErrorMessage = '';
    this.orgConfigErrors = {};
    this.floatingSuccessMessage = '';

    this.orgConfig.climber_app_embed_domains = this.readEmbedDomainsInput();

    this.orgConfigService
      .updateOrganizationConfig(this.orgConfig)
      .then(updatedOrgConf => {
        this.orgConfig = updatedOrgConf;
        // Show what was actually stored: the server lowercases and
        // de-duplicates, so the textarea would otherwise keep displaying the
        // un-tidied version until the page is reloaded.
        this.syncEmbedDomainsInput();
        this.floatingSuccessMessage = 'Settings updated successfully.';
        this.unsavedChanges = false;
      })
      .catch(reason => {
        if (reason.status === 400) {
          this.orgConfigErrors = reason.error;
          this.floatingErrorMessage = 'Please correct the errors shown and try again.';
        } else if (reason.status === 403) {
          this.floatingErrorMessage = 'You do not have permission to modify these settings.';
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => {
        this.loadingSubmit = false;
      });
  }

  public saveMailchimpConfig() {
    this.savingMailchimpConfig = true;

    this.floatingErrorMessage = '';
    this.orgConfigErrors = {};
    this.floatingSuccessMessage = '';

    this.orgConfigService
      .updateMailchimpConfig(this.orgConfig)
      .then(updatedOrgConf => {
        this.orgConfig = updatedOrgConf;
        this.syncEmbedDomainsInput();
        this.floatingSuccessMessage = 'Mailchimp config updated successfully.';
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.floatingErrorMessage = 'You do not have permission to modify these settings.';
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => {
        this.savingMailchimpConfig = false;
      });
  }

  public handleDisconnectLightspeedAccount() {
    this.disconnectLightspeedAccountErrorMessage = '';

    const confirmed = confirm(
      'Are you sure you want to disconnect your Lightspeed account? ' +
      'This will prevent Capitan from being able to interface with your POS.'
    );

    if (!confirmed) { return; }

    this.loadingClearLightspeed = true;

    this.orgConfigService
      .clearLightspeedConnection()
      .then(updatedConfig => {
        this.orgConfig = updatedConfig;
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.disconnectLightspeedAccountErrorMessage = 'You do not have permission to disconnect this Lightspeed account.';
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingClearLightspeed = false; });
  }

  public handleDisconnectSquareAccount() {
    this.disconnectSquareAccountErrorMessage = '';

    const confirmed = confirm(
      'Are you sure you want to disconnect your Square account? ' +
      'This will prevent Capitan from being able to interface with your POS.'
    );

    if (!confirmed) { return; }

    this.loadingClearSquare = true;

    this.orgConfigService
      .clearSquareConnection()
      .then(updatedConfig => {
        this.orgConfig = updatedConfig;
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.disconnectSquareAccountErrorMessage = 'You do not have permission to disconnect this Square account.';
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingClearSquare = false; });
  }

  public handleDisconnectMailchimpAccount() {
    this.mailchimpErrorMessage = '';
    this.mailchimpSuccessMessage = '';

    const confirmed = confirm(
      'Are you sure you want to disconnect your Mailchimp account? ' +
      'This will prevent Capitan from being able to sync customers.'
    );

    if (!confirmed) { return; }

    this.loadingClearMailchimp = true;

    this.orgConfigService
      .clearMailchimpConnection()
      .then(updatedConfig => {
        this.orgConfig = updatedConfig;
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.mailchimpErrorMessage = 'You do not have permission to disconnect this Mailchimp account.';
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingClearMailchimp = false; });
  }

  public handleResyncMailchimpGroups() {
    if (!this.canResyncMailchimpGroups) { return; }

    this.mailchimpErrorMessage = '';
    this.mailchimpSuccessMessage = '';

    this.loadingResyncMailchimpGroups = true;

    this.orgConfigService
      .resyncMailchimpGroups()
      .then(() => {
        this.mailchimpSuccessMessage = 'Mailchimp groups synced successfully.';
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.mailchimpErrorMessage = 'You do not have permission to resync Mailchimp groups.';
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingResyncMailchimpGroups = false; });
  }

  public canResyncMailchimpGroups() {
    return (
      !this.loadingResyncMailchimpGroups &&
      !this.loadingClearMailchimp &&
      !this.orgConfig.mailchimp_audience_id_pending_sync &&
      !!this.orgConfig.mailchimp_audience_id
    );
  }

  public openSetMailchimpAudienceModal() {
    this.modalResult = null;

    const modalRef = this.modalService.open(SetMailchimpAudienceModalComponent);
    modalRef.componentInstance.savedCallback = (result: CallbackResult) => {
      this.modalResult = result;
      this.loadOrganizationConfig();
    }
  }

  public loadingAnyClearConnection() {
    return (
      this.loadingClearStripe ||
      this.loadingClearLightspeed ||
      this.loadingClearSquare ||
      this.loadingClearMailchimp
    );
  }

  public openPreviewEmailOptInModal() {
    const modalRef = this.modalService.open(PreviewEmailOptInModalComponent);

    modalRef.componentInstance.orgConfig = this.orgConfig;
  }

  public openSetSquareLocationMapModal() {
    this.modalResult = null;

    const modalRef = this.modalService.open(SetSquareLocationMapModalComponent);
    modalRef.componentInstance.savedCallback = (result: CallbackResult) => {
      this.modalResult = result;
      this.loadOrganizationConfig();
    }
  }

  public openSetLightspeedLocationMapModal() {
    this.modalResult = null;

    const modalRef = this.modalService.open(SetLightspeedLocationMapModalComponent);
    modalRef.componentInstance.savedCallback = (result: CallbackResult) => {
      this.modalResult = result;
      this.loadOrganizationConfig();
    }
  }

  public openSetSquareCustomerGroupsManagedByCapitanModal() {
    this.modalResult = null;

    const modalRef = this.modalService.open(SetSquareCustomerGroupsManagedByCapitanModalComponent);
    modalRef.componentInstance.savedCallback = (result: CallbackResult) => {
      this.modalResult = result;
      this.loadOrganizationConfig();
    }
  }

  public openSetLightspeedCustomerGroupsManagedByCapitanModal() {
    this.modalResult = null;

    const modalRef = this.modalService.open(SetLightspeedCustomerGroupsManagedByCapitanModalComponent);
    modalRef.componentInstance.savedCallback = (result: CallbackResult) => {
      this.modalResult = result;
      this.loadOrganizationConfig();
    }
  }

  public openSetCapitanLightspeedUserModal() {
    this.modalResult = null;

    const modalRef = this.modalService.open(SetCapitanLightspeedUserModalComponent);
    modalRef.componentInstance.savedCallback = (result: CallbackResult) => {
      this.modalResult = result;
      this.loadOrganizationConfig();
    }
  }

  public isSavingChanges() {
    return (
      this.loadingSubmit ||
      this.savingMailchimpConfig
    );
  }

  public openInviteEmailPreviewModal() {
    const modalRef = this.modalService.open(
      PreviewInviteEmailModalComponent,
      {
        size: 'lg',
      }
    );

    modalRef.componentInstance.orgConfig = this.orgConfig;
  }

}
